import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, ExternalLink, Plus, Pencil, Trash2, X, ImagePlus, Percent, Search, Copy, Pause, Play } from "lucide-react";

const API = "/api";
const fmtCents = (c) => `$${(c / 100).toFixed(2)}`;

const STATUS_COLORS = {
  new: "text-[#E11D2E] border-[#E11D2E]/40",
  read: "text-[#8A8A8A] border-[#1F1F1F]",
  replied: "text-emerald-400 border-emerald-400/30",
  paid: "text-emerald-400 border-emerald-400/30",
  initiated: "text-[#8A8A8A] border-[#1F1F1F]",
  failed: "text-red-400 border-red-400/30",
  expired: "text-[#555555] border-[#1F1F1F]",
  refunded: "text-amber-400 border-amber-400/30",
};

const Badge = ({ value }) => (
  <span className={`border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.18em] ${STATUS_COLORS[value] || "text-[#8A8A8A] border-[#1F1F1F]"}`}>
    {value}
  </span>
);

const inputCls =
  "w-full border border-[#1F1F1F] bg-[#050505] px-3 py-2.5 text-sm text-white placeholder-[#555555] outline-none focus:border-[#E11D2E]";

const EMPTY_FORM = { platform_slug: "", platform_name: "", name: "", quantity: 1000, unit: "followers", price: "", is_bundle: false, tag: "", description: "", requires_comments: false };

const FIELD_LABEL = "mb-1.5 block font-mono text-[10px] uppercase tracking-[0.18em] text-[#8A8A8A]";

function ServiceForm({ platforms, initial, mode, onSave, onCancel, saving }) {
  const [form, setForm] = useState({ ...EMPTY_FORM, ...initial, tag: initial.tag || "", description: initial.description || "" });
  const [newPlatform, setNewPlatform] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onCancel();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const title = mode === "edit" ? `Edit — ${initial.platform_name} · ${initial.name}` : mode === "duplicate" ? `Duplicate — ${initial.name}` : "New service";
  const priceChanged = mode === "edit" && parseFloat(form.price) !== parseFloat(initial.price);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/80 p-4 backdrop-blur-sm sm:items-center" data-testid="service-editor-modal" onMouseDown={(e) => e.target === e.currentTarget && onCancel()}>
      <form
        data-testid="service-form"
        onSubmit={(e) => {
          e.preventDefault();
          onSave({ ...form, quantity: parseInt(form.quantity, 10), price: parseFloat(form.price), tag: form.tag || null });
        }}
        className="my-6 w-full max-w-3xl border border-[#E11D2E]/40 bg-[#0D0D0D] p-6 shadow-[0_0_60px_rgba(225,29,46,0.15)]"
      >
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#E11D2E]">{mode === "edit" ? "Editing service" : "Add to catalog"}</p>
            <h3 className="mt-1 text-lg font-600 text-white" data-testid="service-editor-title">{title}</h3>
          </div>
          <button type="button" data-testid="service-editor-close" onClick={onCancel} className="text-[#8A8A8A] hover:text-white"><X size={18} /></button>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <label className={FIELD_LABEL}>Platform</label>
            <select
              data-testid="service-platform-select"
              value={newPlatform ? "__new__" : form.platform_slug}
              onChange={(e) => {
                if (e.target.value === "__new__") {
                  setNewPlatform(true);
                  setForm((f) => ({ ...f, platform_slug: "", platform_name: "" }));
                } else {
                  setNewPlatform(false);
                  const p = platforms.find((x) => x.slug === e.target.value);
                  setForm((f) => ({ ...f, platform_slug: e.target.value, platform_name: p?.name || "" }));
                }
              }}
              className={inputCls}
              required
            >
              <option value="" disabled>Select platform</option>
              {platforms.map((p) => (
                <option key={p.slug} value={p.slug}>{p.name}</option>
              ))}
              <option value="__new__">+ New platform</option>
            </select>
          </div>
          {newPlatform && (
            <>
              <div>
                <label className={FIELD_LABEL}>New platform name</label>
                <input data-testid="service-new-platform-name" value={form.platform_name} onChange={set("platform_name")} required placeholder="e.g. Reddit" className={inputCls} />
              </div>
              <div>
                <label className={FIELD_LABEL}>Slug (a-z0-9-)</label>
                <input data-testid="service-new-platform-slug" value={form.platform_slug} onChange={set("platform_slug")} required pattern="[a-z0-9-]+" placeholder="e.g. reddit" className={inputCls} />
              </div>
            </>
          )}
          <div>
            <label className={FIELD_LABEL}>Service name</label>
            <input data-testid="service-name-input" value={form.name} onChange={set("name")} required maxLength={120} placeholder="e.g. Followers" className={inputCls} />
          </div>
          <div>
            <label className={FIELD_LABEL}>Quantity per unit</label>
            <input data-testid="service-quantity-input" type="number" min="1" max="10000000" value={form.quantity} onChange={set("quantity")} required className={inputCls} />
          </div>
          <div>
            <label className={FIELD_LABEL}>Unit label</label>
            <input data-testid="service-unit-input" value={form.unit} onChange={set("unit")} required maxLength={40} placeholder="followers / views / plays" className={inputCls} />
          </div>
          <div>
            <label className={FIELD_LABEL}>Price (USD){priceChanged && <span className="ml-2 text-amber-400">· will re-sync Stripe</span>}</label>
            <input data-testid="service-price-input" type="number" min="0.01" step="0.01" value={form.price} onChange={set("price")} required placeholder="9.99" className={inputCls} />
          </div>
          <div>
            <label className={FIELD_LABEL}>Status</label>
            <select data-testid="service-status-select" value={form.status || "active"} onChange={set("status")} className={inputCls}>
              <option value="active">Active — visible to buyers</option>
              <option value="paused">Paused — hidden from catalog</option>
            </select>
          </div>
          <label className="flex items-center gap-2 self-end pb-2 font-mono text-[11px] uppercase tracking-[0.14em] text-[#8A8A8A]">
            <input data-testid="service-bundle-checkbox" type="checkbox" checked={!!form.is_bundle} onChange={set("is_bundle")} className="accent-[#E11D2E]" />
            Targeted bundle
          </label>
          {form.is_bundle && (
            <div>
              <label className={FIELD_LABEL}>Bundle tag</label>
              <input data-testid="service-tag-input" value={form.tag} onChange={set("tag")} maxLength={40} placeholder="e.g. Targeted / Best value" className={inputCls} />
            </div>
          )}
          <div className="sm:col-span-2 lg:col-span-3">
            <label className={FIELD_LABEL}>Description (shown to buyers on the order page) <span className="normal-case tracking-normal text-[#555555]">{(form.description || "").length}/1000</span></label>
            <textarea data-testid="service-description-input" value={form.description} onChange={set("description")} maxLength={1000} rows={3} placeholder="What the buyer gets, delivery speed, retention, anything they should know…" className={`${inputCls} resize-y`} />
          </div>
          <label className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.14em] text-[#8A8A8A] sm:col-span-2 lg:col-span-3">
            <input data-testid="service-requires-comments-checkbox" type="checkbox" checked={!!form.requires_comments} onChange={set("requires_comments")} className="accent-[#E11D2E]" />
            Requires custom comments (buyer must type what the comments should say)
          </label>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-2 border-t border-[#1F1F1F] pt-5">
          <button data-testid="service-save-btn" type="submit" disabled={saving} className="bg-[#E11D2E] px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.14em] text-white hover:bg-[#F13B47] disabled:opacity-50">
            {saving ? "Saving…" : mode === "edit" ? "Save changes" : "Create service"}
          </button>
          <button type="button" data-testid="service-cancel-btn" onClick={onCancel} className="border border-[#1F1F1F] px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.14em] text-[#8A8A8A] hover:text-white">
            Cancel
          </button>
          {mode === "edit" && <span className="ml-auto font-mono text-[10px] text-[#555555]">ID {initial.id.slice(0, 8)} · Esc to close</span>}
        </div>
      </form>
    </div>
  );
}

function SettingsPanel() {
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error("New passwords don't match");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: current, new_password: next }, { withCredentials: true });
      toast.success("Password changed — sign in again");
      setTimeout(() => navigate("/admin"), 1200);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Change failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form data-testid="change-password-form" onSubmit={submit} className="mt-4 space-y-4">
      <div>
        <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.18em] text-[#8A8A8A]">Current password</label>
        <input data-testid="current-password-input" type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} className={inputCls} />
      </div>
      <div>
        <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.18em] text-[#8A8A8A]">New password (min 8 chars)</label>
        <input data-testid="new-password-input" type="password" required minLength={8} value={next} onChange={(e) => setNext(e.target.value)} className={inputCls} />
      </div>
      <div>
        <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.18em] text-[#8A8A8A]">Confirm new password</label>
        <input data-testid="confirm-password-input" type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} />
      </div>
      <button data-testid="change-password-submit" type="submit" disabled={busy} className="bg-[#E11D2E] px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.14em] text-white hover:bg-[#F13B47] disabled:opacity-50">
        {busy ? "Saving…" : "Change password"}
      </button>
      <p className="font-mono text-[11px] leading-relaxed text-[#555555]">You'll be signed out after changing it.</p>
    </form>
  );
}

const EMAIL_STATUS_COLORS = {
  sent: "text-emerald-400 border-emerald-400/30",
  pending: "text-amber-400 border-amber-400/30",
  failed: "text-red-400 border-red-400/30",
};

function EmailLog() {
  const [data, setData] = useState(null);

  const load = () =>
    axios.get(`${API}/admin/email-jobs`, { withCredentials: true })
      .then((res) => setData(res.data)).catch(() => setData({ jobs: [], failed_count: 0, provider: null }));

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const sendTest = async () => {
    try {
      await axios.post(`${API}/admin/email-test`, {}, { withCredentials: true });
      toast.success("Test email queued — it should land within a minute");
      load();
    } catch {
      toast.error("Could not queue test email");
    }
  };

  const retry = async (id) => {
    try {
      await axios.post(`${API}/admin/email-jobs/${id}/retry`, {}, { withCredentials: true });
      toast.success("Email re-queued");
      load();
    } catch {
      toast.error("Retry failed");
    }
  };

  if (data === null) return <p className="font-mono text-xs text-[#555555]">Loading email log…</p>;
  const { jobs, failed_count: failedCount, provider } = data;

  return (
    <div className="space-y-4">
      {provider && (
        <div data-testid="email-provider-info" className="flex flex-wrap items-center justify-between gap-3 border border-[#1F1F1F] bg-[#0D0D0D] px-4 py-3">
          <div className="font-mono text-[11px] text-[#8A8A8A]">
            <span className="uppercase tracking-[0.18em] text-[#555555]">Sender</span>{" "}
            <span className="text-white">{provider.from}</span>
            <span className="ml-2 border border-[#1F1F1F] px-1.5 py-0.5 text-[10px] uppercase tracking-[0.14em]">{provider.provider === "resend" ? "Resend · cybersmokemedia.com" : "Managed"}</span>
            <span className="ml-3 uppercase tracking-[0.18em] text-[#555555]">Alerts to</span>{" "}
            <span className="text-white">{provider.admin_recipients.join(", ")}</span>
          </div>
          <button data-testid="send-test-email-btn" onClick={sendTest} className="border border-[#1F1F1F] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-[#8A8A8A] hover:border-[#E11D2E] hover:text-white">Send test email</button>
        </div>
      )}
      {failedCount > 0 && (
        <div data-testid="email-failed-banner" className="border border-red-500/40 bg-red-950/30 px-4 py-3 font-mono text-xs text-red-300">
          {failedCount} email{failedCount > 1 ? "s" : ""} failed after all retries — use Retry below or check the error.
        </div>
      )}
      {jobs.length === 0 ? (
        <p className="font-mono text-xs text-[#555555]">No emails queued yet.</p>
      ) : (
        <div className="space-y-2" data-testid="email-jobs-list">
          {jobs.map((j) => (
            <div key={j.id} data-testid={`email-job-${j.id}`} className="flex flex-wrap items-center justify-between gap-3 border border-[#1F1F1F] bg-[#0D0D0D] px-4 py-3">
              <div className="min-w-0">
                <p className="truncate text-sm text-white">{j.subject}</p>
                <p className="mt-0.5 font-mono text-[11px] text-[#8A8A8A]">
                  {j.kind} → {Array.isArray(j.to) ? j.to.join(", ") : j.to}
                  {j.provider && <span className="ml-2 text-[#555555]">via {j.provider}</span>}
                </p>
                {j.last_error && <p className="mt-0.5 font-mono text-[11px] text-red-400">{j.last_error}</p>}
              </div>
              <div className="flex items-center gap-3">
                {j.status === "failed" && (
                  <button data-testid={`email-retry-${j.id}`} onClick={() => retry(j.id)} className="border border-[#1F1F1F] px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.14em] text-[#8A8A8A] hover:border-white hover:text-white">Retry</button>
                )}
                <span className={`border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.18em] ${EMAIL_STATUS_COLORS[j.status] || "text-[#8A8A8A] border-[#1F1F1F]"}`}>
                  {j.status}{j.attempts > 0 ? ` ×${j.attempts}` : ""}
                </span>
                <span className="font-mono text-[11px] text-[#555555]">
                  {new Date(j.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("enquiries");
  const [enquiries, setEnquiries] = useState([]);
  const [orders, setOrders] = useState([]);
  const [services, setServices] = useState([]);
  const [platformIcons, setPlatformIcons] = useState([]);
  const [payouts, setPayouts] = useState(null);
  const [notes, setNotes] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editMode, setEditMode] = useState("new");
  const [serviceQuery, setServiceQuery] = useState("");
  const [servicePlatform, setServicePlatform] = useState("all");
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [bulkPct, setBulkPct] = useState(10);
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [iconBusy, setIconBusy] = useState(null);

  useEffect(() => {
    const check = async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(data);
      } catch {
        try {
          await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
          const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
          setUser(data);
        } catch {
          navigate("/admin");
        }
      }
    };
    check();
  }, [navigate]);

  const loadServices = () =>
    axios.get(`${API}/admin/services`, { withCredentials: true })
      .then((res) => setServices(res.data.services)).catch(() => {});

  useEffect(() => {
    if (!user) return;
    if (tab === "enquiries") {
      axios.get(`${API}/admin/enquiries`, { withCredentials: true })
        .then((res) => setEnquiries(res.data.enquiries)).catch(() => {});
    } else if (tab === "orders") {
      axios.get(`${API}/admin/orders`, { withCredentials: true })
        .then((res) => setOrders(res.data.orders)).catch(() => {});
    } else if (tab === "services") {
      loadServices();
      axios.get(`${API}/admin/platforms`, { withCredentials: true })
        .then((res) => setPlatformIcons(res.data.platforms)).catch(() => {});
    } else if (tab === "payouts") {
      axios.get(`${API}/admin/payouts`, { withCredentials: true })
        .then((res) => setPayouts(res.data)).catch(() => setPayouts({ error: true }));
    }
  }, [user, tab]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/enquiries/${id}`, { status }, { withCredentials: true });
      setEnquiries((list) => list.map((e) => (e.id === id ? { ...e, status } : e)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Update failed");
    }
  };

  const saveNote = async (id) => {
    try {
      await axios.patch(`${API}/admin/enquiries/${id}`, { note: notes[id] || "" }, { withCredentials: true });
      toast.success("Note saved");
    } catch {
      toast.error("Note failed");
    }
  };

  const saveService = async (form) => {
    setSaving(true);
    try {
      if (editMode === "edit" && editing) {
        const fields = ["platform_slug", "platform_name", "name", "quantity", "unit", "price", "is_bundle", "tag", "description", "requires_comments", "status"];
        const payload = Object.fromEntries(fields.filter((k) => k in form).map((k) => [k, form[k]]));
        await axios.patch(`${API}/admin/services/${editing.id}`, payload, { withCredentials: true });
        toast.success(`Saved — ${form.name}${form.price !== editing.price ? " · Stripe price re-synced" : ""}`);
      } else {
        const { id, status, sort_order, created_at, updated_at, ...payload } = form;
        await axios.post(`${API}/admin/services`, payload, { withCredentials: true });
        toast.success(`${form.name} added — Stripe price created`);
      }
      closeEditor();
      loadServices();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map((d) => d.msg).join("; ") : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const openEditor = (service, mode) => {
    setEditing(service ? { ...service } : null);
    setEditMode(mode);
    setShowForm(true);
  };

  const closeEditor = () => {
    setShowForm(false);
    setEditing(null);
    setEditMode("new");
  };

  const toggleService = async (s) => {
    const status = s.status === "paused" ? "active" : "paused";
    try {
      await axios.patch(`${API}/admin/services/${s.id}`, { status }, { withCredentials: true });
      setServices((list) => list.map((x) => (x.id === s.id ? { ...x, status } : x)));
      toast.success(status === "paused" ? `${s.name} paused — hidden from buyers` : `${s.name} is live again`);
    } catch {
      toast.error("Could not change status");
    }
  };

  const visibleServices = services.filter((s) => {
    if (servicePlatform === "__bundles" && !s.is_bundle) return false;
    if (servicePlatform !== "all" && servicePlatform !== "__bundles" && s.platform_slug !== servicePlatform) return false;
    if (!serviceQuery.trim()) return true;
    const q = serviceQuery.toLowerCase();
    return [s.name, s.platform_name, s.unit, s.tag, s.description].some((v) => (v || "").toLowerCase().includes(q));
  });

  const deleteService = async (id) => {
    try {
      await axios.delete(`${API}/admin/services/${id}`, { withCredentials: true });
      toast.success("Service removed");
      setConfirmDelete(null);
      loadServices();
    } catch {
      toast.error("Delete failed");
    }
  };

  const applyBulk = async () => {
    setBulkBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/services/bulk-price`, { percent: bulkPct }, { withCredentials: true });
      toast.success(`${data.updated} prices updated by ${bulkPct > 0 ? "+" : ""}${bulkPct}% and synced to Stripe`);
      setBulkConfirm(false);
      loadServices();
    } catch {
      toast.error("Bulk update failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const uploadIcon = async (slug, file) => {
    if (!file) return;
    setIconBusy(slug);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/admin/platforms/${slug}/icon`, fd, { withCredentials: true });
      toast.success("Icon uploaded");
      const { data } = await axios.get(`${API}/admin/platforms`, { withCredentials: true });
      setPlatformIcons(data.platforms);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed");
    } finally {
      setIconBusy(null);
    }
  };

  const setOrderStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/orders/${id}`, { status }, { withCredentials: true });
      setOrders((list) => list.map((o) => (o.id === id ? { ...o, status } : o)));
      toast.success(`Order marked ${status} — customer emailed`);
    } catch {
      toast.error("Status update failed");
    }
  };

  const resendReceipt = async (id) => {
    try {
      await axios.post(`${API}/admin/orders/${id}/resend-receipt`, {}, { withCredentials: true });
      toast.success("Receipt + admin alert re-queued — check the Emails tab");
    } catch {
      toast.error("Resend failed");
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    navigate("/admin");
  };

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#050505]" data-testid="admin-loading">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#8A8A8A]">Checking access…</p>
      </div>
    );
  }

  const platforms = [];
  const seen = new Set();
  for (const s of services) {
    if (!s.is_bundle && !seen.has(s.platform_slug)) {
      seen.add(s.platform_slug);
      platforms.push({ slug: s.platform_slug, name: s.platform_name });
    }
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-dashboard">
      <header className="border-b border-[#1F1F1F] bg-[#0D0D0D]/70 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-6">
          <div className="flex items-center gap-2.5">
            <span className="h-2.5 w-2.5 bg-[#E11D2E]" />
            <span className="font-display text-sm font-800 tracking-[0.22em] text-white">INBOX</span>
            <span className="font-mono text-[10px] tracking-[0.2em] text-[#8A8A8A]">{user.email}</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-view-site" className="flex items-center gap-1.5 font-mono text-xs text-[#8A8A8A] hover:text-white">
              <ExternalLink size={13} /> View site
            </Link>
            <button data-testid="admin-logout" onClick={logout} className="flex items-center gap-1.5 border border-[#1F1F1F] px-4 py-2 font-mono text-xs text-white hover:border-[#333333]">
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-6 py-10">
        <div className="flex flex-wrap gap-2" data-testid="admin-tabs">
          {["enquiries", "orders", "services", "payouts", "emails", "settings"].map((t) => (
            <button
              key={t}
              data-testid={`tab-${t}`}
              onClick={() => setTab(t)}
              className={`px-5 py-2.5 font-mono text-xs uppercase tracking-[0.14em] transition-colors ${tab === t ? "bg-[#E11D2E] text-white" : "border border-[#1F1F1F] text-[#8A8A8A] hover:text-white"}`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "enquiries" && (
          <div className="mt-8 space-y-4" data-testid="enquiries-list">
            {enquiries.length === 0 && <p className="font-mono text-xs text-[#555555]">No enquiries yet.</p>}
            {enquiries.map((e) => (
              <div key={e.id} data-testid={`enquiry-${e.id}`} className="border border-[#1F1F1F] bg-[#0D0D0D] p-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <span className="text-sm font-600 text-white">{e.name}</span>
                    <span className="ml-3 font-mono text-xs text-[#8A8A8A]">{e.email}</span>
                    {e.interest && <span className="ml-3 font-mono text-xs text-[#E11D2E]">{e.interest}</span>}
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge value={e.status} />
                    <span className="font-mono text-[11px] text-[#555555]">{new Date(e.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
                  </div>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-[#8A8A8A]">{e.message}</p>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <input
                    data-testid={`note-input-${e.id}`}
                    value={notes[e.id] ?? e.note ?? ""}
                    onChange={(ev) => setNotes((n) => ({ ...n, [e.id]: ev.target.value }))}
                    placeholder="Internal note…"
                    className="min-w-56 flex-1 border border-[#1F1F1F] bg-[#050505] px-3 py-2 text-xs text-white placeholder-[#555555] outline-none focus:border-[#E11D2E]"
                  />
                  <button data-testid={`note-save-${e.id}`} onClick={() => saveNote(e.id)} className="border border-[#1F1F1F] px-3 py-2 font-mono text-[11px] uppercase tracking-[0.14em] text-white hover:border-[#333333]">Save note</button>
                  <button data-testid={`mark-read-${e.id}`} onClick={() => setStatus(e.id, "read")} className="border border-[#1F1F1F] px-3 py-2 font-mono text-[11px] uppercase tracking-[0.14em] text-[#8A8A8A] hover:text-white">Mark read</button>
                  <button data-testid={`mark-replied-${e.id}`} onClick={() => setStatus(e.id, "replied")} className="bg-[#E11D2E] px-3 py-2 font-mono text-[11px] uppercase tracking-[0.14em] text-white hover:bg-[#F13B47]">Mark replied</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "orders" && (
          <div className="mt-8 space-y-4" data-testid="orders-list">
            {orders.length === 0 && <p className="font-mono text-xs text-[#555555]">No orders yet.</p>}
            {orders.map((o) => (
              <div key={o.id} data-testid={`order-${o.id}`} className="border border-[#1F1F1F] bg-[#0D0D0D] p-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <span className="font-mono text-sm font-600 text-white">{o.order_number}</span>
                  <Badge value={o.status} />
                </div>
                <div className="mt-4 grid gap-2 font-mono text-xs text-[#8A8A8A] sm:grid-cols-2">
                  <span>{o.platform} — {o.service_name}</span>
                  <span>{o.unit_label ? `${(o.total_units ?? o.quantity_units * 1000).toLocaleString("en-US")} ${o.unit_label}` : "Bundle"}</span>
                  <span className="text-white">{fmtCents(o.amount)} {String(o.currency).toUpperCase()}</span>
                  <span>{o.email}</span>
                  <span className="truncate sm:col-span-2">{o.target_url}</span>
                </div>
                {o.custom_comments && (
                  <div className="mt-3 border border-[#1F1F1F] bg-[#151515] p-3" data-testid={`order-comments-${o.id}`}>
                    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#8A8A8A]">Custom comments</p>
                    <p className="mt-1 whitespace-pre-wrap text-sm text-white">{o.custom_comments}</p>
                  </div>
                )}
                {(o.status === "paid" || o.status === "processing" || o.status === "delayed") && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {o.status !== "processing" && (
                      <button data-testid={`order-processing-${o.id}`} onClick={() => setOrderStatus(o.id, "processing")} className="border border-[#1F1F1F] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-[#8A8A8A] hover:border-[#E11D2E] hover:text-white">Mark processing</button>
                    )}
                    {o.status !== "complete" && (
                      <button data-testid={`order-complete-${o.id}`} onClick={() => setOrderStatus(o.id, "complete")} className="bg-[#E11D2E] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-white hover:bg-[#F13B47]">Mark complete</button>
                    )}
                    {o.status !== "delayed" && (
                      <button data-testid={`order-delayed-${o.id}`} onClick={() => setOrderStatus(o.id, "delayed")} className="border border-[#1F1F1F] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-[#8A8A8A] hover:border-amber-400 hover:text-amber-400">Mark delayed</button>
                    )}
                    <button data-testid={`order-resend-receipt-${o.id}`} onClick={() => resendReceipt(o.id)} className="border border-[#1F1F1F] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-[#8A8A8A] hover:border-white hover:text-white">Resend emails</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === "services" && (
          <div className="mt-8" data-testid="services-tab">
            <div className="mb-6 flex items-center justify-between">
              <p className="font-mono text-xs text-[#8A8A8A]">{services.length} services — price edits sync to Stripe automatically</p>
              <button
                data-testid="add-service-btn"
                onClick={() => openEditor(null, "new")}
                className="flex items-center gap-2 bg-[#E11D2E] px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.14em] text-white hover:bg-[#F13B47]"
              >
                <Plus size={13} /> Add service
              </button>
            </div>

            <div className="mb-6 border border-[#1F1F1F] bg-[#0D0D0D] p-5" data-testid="bulk-price-panel">
              <div className="flex flex-wrap items-center gap-4">
                <Percent size={16} className="text-[#E11D2E]" />
                <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#8A8A8A]">Bulk price edit</p>
                <input
                  data-testid="bulk-slider"
                  type="range"
                  min="-50"
                  max="100"
                  step="1"
                  value={bulkPct}
                  onChange={(e) => { setBulkPct(parseInt(e.target.value, 10)); setBulkConfirm(false); }}
                  className="w-48 accent-[#E11D2E]"
                />
                <span className="font-mono text-sm font-600 text-white" data-testid="bulk-value">{bulkPct > 0 ? "+" : ""}{bulkPct}%</span>
                {bulkConfirm ? (
                  <>
                    <button data-testid="bulk-confirm" onClick={applyBulk} disabled={bulkBusy} className="bg-[#E11D2E] px-4 py-2 font-mono text-[10px] uppercase tracking-[0.14em] text-white disabled:opacity-50">
                      {bulkBusy ? "Applying…" : "Confirm apply to all"}
                    </button>
                    <button data-testid="bulk-cancel" onClick={() => setBulkConfirm(false)} className="border border-[#1F1F1F] px-4 py-2 font-mono text-[10px] uppercase text-[#8A8A8A]">Cancel</button>
                  </>
                ) : (
                  <button data-testid="bulk-apply" onClick={() => setBulkConfirm(true)} disabled={bulkPct === 0} className="border border-[#E11D2E] px-4 py-2 font-mono text-[10px] uppercase tracking-[0.14em] text-[#E11D2E] hover:bg-[#E11D2E] hover:text-white disabled:opacity-40">
                    Apply to all services
                  </button>
                )}
              </div>
            </div>

            <div className="mb-6 border border-[#1F1F1F] bg-[#0D0D0D] p-5" data-testid="platform-icons-panel">
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#8A8A8A]">Platform icons</p>
              <div className="mt-4 flex flex-wrap gap-3">
                {platformIcons.map((p) => (
                  <label
                    key={p.slug}
                    data-testid={`icon-upload-${p.slug}`}
                    className="flex cursor-pointer flex-col items-center gap-2 border border-[#1F1F1F] px-4 py-3 transition-colors hover:border-[#E11D2E]"
                  >
                    {p.icon_url ? (
                      <img src={p.icon_url} alt={p.name} className="h-8 w-8 object-contain" />
                    ) : (
                      <ImagePlus size={20} className="text-[#555555]" />
                    )}
                    <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-[#8A8A8A]">
                      {iconBusy === p.slug ? "Uploading…" : p.name}
                    </span>
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadIcon(p.slug, e.target.files[0])} />
                  </label>
                ))}
              </div>
            </div>

            {showForm && (
              <ServiceForm
                key={editing ? `${editMode}-${editing.id}` : "new"}
                platforms={platforms}
                initial={editing || EMPTY_FORM}
                mode={editMode}
                saving={saving}
                onSave={saveService}
                onCancel={closeEditor}
              />
            )}

            <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="services-toolbar">
              <div className="relative flex-1 min-w-[220px]">
                <Search size={13} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#555555]" />
                <input
                  data-testid="services-search"
                  value={serviceQuery}
                  onChange={(e) => setServiceQuery(e.target.value)}
                  placeholder="Search services by name, platform, unit…"
                  className={`${inputCls} pl-9`}
                />
              </div>
              <select data-testid="services-platform-filter" value={servicePlatform} onChange={(e) => setServicePlatform(e.target.value)} className={`${inputCls.replace("w-full ", "")} w-full sm:w-56`}>
                <option value="all">All platforms</option>
                <option value="__bundles">Bundles</option>
                {platforms.map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
              </select>
              <span className="font-mono text-[11px] text-[#555555]" data-testid="services-count">{visibleServices.length} / {services.length}</span>
            </div>

            <div className="space-y-2" data-testid="services-list">
              {visibleServices.length === 0 && (
                <p className="py-8 text-center font-mono text-xs text-[#555555]" data-testid="services-empty">No services match — clear the search or add one.</p>
              )}
              {visibleServices.map((s) => (
                <div key={s.id} data-testid={`admin-service-${s.id}`} className={`flex flex-wrap items-center justify-between gap-3 border bg-[#0D0D0D] px-4 py-3 ${s.status === "paused" ? "border-amber-400/30 opacity-75" : "border-[#1F1F1F]"}`}>
                  <div className="flex min-w-0 flex-wrap items-baseline gap-3">
                    <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-[#E11D2E]">{s.is_bundle ? "Bundle" : s.platform_name}</span>
                    <span className="text-sm font-600 text-white">{s.name}</span>
                    <span className="font-mono text-[11px] text-[#8A8A8A]">{s.quantity.toLocaleString("en-US")} {s.unit}</span>
                    {s.status === "paused" && <span className="border border-amber-400/40 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.18em] text-amber-400" data-testid={`paused-badge-${s.id}`}>Paused</span>}
                    {s.requires_comments && <span className="border border-[#1F1F1F] px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.18em] text-[#8A8A8A]">Comments</span>}
                    {s.description && <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#555555]" title={s.description}>Desc ✓</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-600 text-white">${s.price.toFixed(2)}</span>
                    <button
                      data-testid={`edit-service-${s.id}`}
                      onClick={() => openEditor(s, "edit")}
                      className="flex items-center gap-1 border border-[#1F1F1F] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-[#8A8A8A] hover:border-[#E11D2E] hover:text-white"
                    >
                      <Pencil size={11} /> Edit
                    </button>
                    <button
                      data-testid={`duplicate-service-${s.id}`}
                      onClick={() => openEditor(s, "duplicate")}
                      title="Duplicate"
                      className="border border-[#1F1F1F] px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-[#8A8A8A] hover:border-white hover:text-white"
                    >
                      <Copy size={11} />
                    </button>
                    <button
                      data-testid={`toggle-service-${s.id}`}
                      onClick={() => toggleService(s)}
                      title={s.status === "paused" ? "Resume" : "Pause"}
                      className={`border px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] ${s.status === "paused" ? "border-emerald-400/40 text-emerald-400 hover:bg-emerald-400/10" : "border-[#1F1F1F] text-[#8A8A8A] hover:border-amber-400 hover:text-amber-400"}`}
                    >
                      {s.status === "paused" ? <Play size={11} /> : <Pause size={11} />}
                    </button>
                    {confirmDelete === s.id ? (
                      <>
                        <button data-testid={`confirm-delete-${s.id}`} onClick={() => deleteService(s.id)} className="flex items-center gap-1 bg-[#E11D2E] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-white">
                          <Trash2 size={11} /> Confirm
                        </button>
                        <button data-testid={`cancel-delete-${s.id}`} onClick={() => setConfirmDelete(null)} className="border border-[#1F1F1F] px-3 py-1.5 font-mono text-[10px] uppercase text-[#8A8A8A]">
                          <X size={11} />
                        </button>
                      </>
                    ) : (
                      <button data-testid={`delete-service-${s.id}`} onClick={() => setConfirmDelete(s.id)} className="flex items-center gap-1 border border-[#1F1F1F] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-[#8A8A8A] hover:border-red-400 hover:text-red-400">
                        <Trash2 size={11} /> Delete
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "emails" && (
          <div className="mt-8" data-testid="emails-tab">
            <p className="mb-4 font-mono text-xs text-[#8A8A8A]">
              Every email the system sends — with delivery status. Refreshes automatically.
            </p>
            <EmailLog />
          </div>
        )}

        {tab === "settings" && (
          <div className="mt-8 max-w-md" data-testid="settings-tab">
            <div className="border border-[#1F1F1F] bg-[#0D0D0D] p-6">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#E11D2E]">Change admin password</p>
              <SettingsPanel />
            </div>
          </div>
        )}

        {tab === "payouts" && (
          <div className="mt-8" data-testid="payouts-tab">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="border border-[#1F1F1F] bg-[#0D0D0D] p-6">
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#8A8A8A]">Available balance</p>
                <p className="mt-3 font-mono text-3xl font-600 text-white" data-testid="payouts-available">
                  {payouts?.available?.length ? fmtCents(payouts.available[0].amount) : "$0.00"}
                  <span className="ml-2 text-sm text-[#8A8A8A]">USD</span>
                </p>
              </div>
              <div className="border border-[#1F1F1F] bg-[#0D0D0D] p-6">
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#8A8A8A]">Pending</p>
                <p className="mt-3 font-mono text-3xl font-600 text-white" data-testid="payouts-pending">
                  {payouts?.pending?.length ? fmtCents(payouts.pending[0].amount) : "$0.00"}
                  <span className="ml-2 text-sm text-[#8A8A8A]">USD</span>
                </p>
              </div>
            </div>
            <div className="mt-6 border border-[#E11D2E]/30 bg-[#0D0D0D] p-6" data-testid="payouts-setup-note">
              <p className="font-mono text-xs uppercase tracking-[0.18em] text-[#E11D2E]">Payout method</p>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-[#8A8A8A]">
                {payouts?.livemode
                  ? "Your Stripe account is live. Manage your bank account and payout schedule in the Stripe Dashboard."
                  : "You're on a test sandbox. To receive real payouts: claim your Stripe account (one click from the onboarding link), complete verification (KYC), then add your bank account under Settings → Payouts in the Stripe Dashboard. Payouts then land automatically on Stripe's schedule."}
              </p>
              <a
                data-testid="payouts-stripe-link"
                href="https://dashboard.stripe.com/payouts"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 border border-[#E11D2E] px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.14em] text-white transition-colors hover:bg-[#E11D2E]"
              >
                Open Stripe payouts <ExternalLink size={12} />
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
